import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  fetchMilestones,
  createMilestone,
  updateMilestone,
  completeMilestone,
  deleteMilestone,
} from '../../store/actions/Projects/ProjectActions';
import Button from '../shared/TailwindComponents/Button';
import GlassCard from '../shared/brand/GlassCard';
import PaneFooter from '../shared/surface/PaneFooter';
import { useInPane } from '../shared/surface/PaneBody';
import Badge from '../shared/TailwindComponents/Badge';
import { formatDateTime, parseServerDate } from '../../utils/functions';
import {
  PiPlusLight,
  PiCheckCircleLight,
  PiCircleDashedLight,
  PiTrashLight,
  PiPencilSimpleLight,
  PiXLight,
  PiCheckLight,
  PiArrowClockwiseLight,
  PiFlagPennantLight,
} from 'react-icons/pi';

interface ProjectMilestonesProps {
  projectUuid: string;
  /**
   * Controlled "add" state, for a host that renders the add button itself.
   *
   * In a pane the add button belongs next to the close × (the pane's `actions`
   * slot), which is outside this component — so the pane owns the flag and
   * this list only reads it. Left undefined, the list keeps its own state and
   * its own button, the way it works on a canvas.
   */
  isAdding?: boolean;
  onIsAddingChange?: (next: boolean) => void;
}

const STATUS_CONFIG: Record<string, { icon: React.ReactElement; color: string; bg: string; label: string }> = {
  pending: { icon: <PiCircleDashedLight className="w-4 h-4" />, color: 'text-gray-400', bg: 'bg-surface-sunken', label: 'Pending' },
  in_progress: { icon: <PiArrowClockwiseLight className="w-4 h-4" />, color: 'text-info-500', bg: 'bg-info-50 dark:bg-info-900/30', label: 'In Progress' },
  completed: { icon: <PiCheckCircleLight className="w-4 h-4" />, color: 'text-success-500', bg: 'bg-success-50 dark:bg-success-900/30', label: 'Completed' },
  skipped: { icon: <PiXLight className="w-4 h-4" />, color: 'text-gray-400', bg: 'bg-surface-sunken', label: 'Skipped' },
};

/**
 * The card chrome is NOT a prop any more.
 *
 * This used to take `bare` so the project surface's Milestones pane could ask
 * for a headerless list — a pane already draws a glass box, a title and a
 * close button, so a card inside one is a card in a card. Whether it is in a
 * pane is a fact about where it is rendered, not a caller's preference, so it
 * is read from `useInPane()` instead. See `shared/surface/PaneBody`.
 *
 * `GlassCard paneAware` was not enough: it dropped the glass and KEPT the
 * header row, which is how the pane ended up with "Milestones" written twice.
 * In a pane the body renders alone, under the pane's own title.
 */
const ProjectMilestones: React.FC<ProjectMilestonesProps> = ({
  projectUuid,
  isAdding: isAddingProp,
  onIsAddingChange,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const inPane = useInPane();

  const { milestones, isLoadingMilestones } = useSelector((state: RootState) => state.projects);

  const [ownIsAdding, setOwnIsAdding] = useState(false);
  const isAdding = isAddingProp ?? ownIsAdding;
  const setIsAdding = (next: boolean) => {
    setOwnIsAdding(next);
    onIsAddingChange?.(next);
  };
  const [newTitle, setNewTitle] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDueDate, setEditDueDate] = useState('');

  useEffect(() => {
    dispatch(fetchMilestones(projectUuid));
  }, [dispatch, projectUuid]);

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    const result = await dispatch(createMilestone(projectUuid, {
      title: newTitle.trim(),
      due_date: newDueDate || undefined,
    }));
    if (result) {
      setNewTitle('');
      setNewDueDate('');
      setIsAdding(false);
    }
  };

  const handleStartEdit = (milestone: any) => {
    setEditingId(milestone.id);
    setEditTitle(milestone.title || '');
    setEditDueDate(milestone.due_date ? milestone.due_date.split('T')[0] : '');
  };

  const handleSaveEdit = async (milestoneId: number) => {
    if (!editTitle.trim()) return;
    await dispatch(updateMilestone(projectUuid, milestoneId, {
      title: editTitle.trim(),
      due_date: editDueDate || undefined,
    }));
    setEditingId(null);
  };

  const handleStatusChange = async (milestoneId: number, newStatus: string) => {
    if (newStatus === 'completed') {
      await dispatch(completeMilestone(projectUuid, milestoneId));
    } else {
      await dispatch(updateMilestone(projectUuid, milestoneId, { status: newStatus }));
    }
  };

  const handleDelete = async (milestoneId: number) => {
    await dispatch(deleteMilestone(projectUuid, milestoneId));
  };

  // Completion stats
  const totalCount = milestones.length;
  const completedCount = milestones.filter((m: any) => m.status === 'completed').length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const getDueDateUrgency = (dueDate: string | null) => {
    if (!dueDate) return null;
    const d = parseServerDate(dueDate);
    if (!d) return null;
    const days = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (days < 0) return 'overdue';
    if (days <= 3) return 'soon';
    return null;
  };

  const addButton = (
    <Button variant="neutral" size="small" onClick={() => setIsAdding(true)}>
      <PiPlusLight className="h-3.5 w-3.5 mr-1" />{t('Add')}
    </Button>
  );

  const tally = totalCount > 0 ? `${completedCount}/${totalCount}` : null;

  const body = (
    <>
      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="px-5 pt-3">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-surface-sunken rounded-full overflow-hidden">
              <div
                className="h-full bg-success-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">{progressPercent}%</span>
          </div>
        </div>
      )}

      {/* Milestone list */}
      <div className="p-4">
        {isLoadingMilestones ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-3 items-start">
                <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 animate-fadePulse flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
                  <div className="h-2.5 w-1/3 bg-surface-sunken/70 rounded animate-fadePulse" />
                </div>
              </div>
            ))}
          </div>
        ) : milestones.length === 0 && !isAdding ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
            {t('No milestones yet. Add phases to track progress.')}
          </p>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            {milestones.length > 0 && (
              <div className="absolute left-[9px] top-2 bottom-2 w-px bg-hairline" />
            )}

            <div className="space-y-0">
              {milestones.map((milestone: any, index: number) => {
                const config = STATUS_CONFIG[milestone.status] || STATUS_CONFIG.pending;
                const isEditing = editingId === milestone.id;
                const urgency = getDueDateUrgency(milestone.due_date);
                const isCompleted = milestone.status === 'completed';

                return (
                  <div key={milestone.id} className="relative flex gap-3 group py-2">
                    {/* Status icon - clickable to toggle complete */}
                    <button
                      onClick={() => handleStatusChange(milestone.id, isCompleted ? 'pending' : 'completed')}
                      className={clsx(
                        'relative z-10 w-[18px] h-[18px] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all border',
                        isCompleted
                          ? 'bg-success-500 border-success-500 text-white'
                          : 'bg-surface border-hairline hover:border-success-400 hover:bg-success-50 dark:hover:bg-success-900/20'
                      )}
                      title={isCompleted ? t('Mark as pending') : t('Mark as complete')}
                    >
                      {isCompleted && <PiCheckLight className="w-3 h-3" />}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-hairline rounded bg-surface-sunken text-fg-body focus:ring-1 focus:ring-primary-500"
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(milestone.id); if (e.key === 'Escape') setEditingId(null); }}
                          />
                          <input
                            type="date"
                            value={editDueDate}
                            onChange={(e) => setEditDueDate(e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-hairline rounded bg-surface-sunken text-fg-body"
                          />
                          <div className="flex gap-1.5">
                            <Button variant="primary" size="small" onClick={() => handleSaveEdit(milestone.id)}>{t('Save')}</Button>
                            <Button variant="neutral" size="small" onClick={() => setEditingId(null)}>{t('Cancel')}</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className={clsx(
                            'text-sm leading-snug',
                            isCompleted ? 'text-gray-400 dark:text-gray-500 line-through' : 'text-gray-800 dark:text-gray-200'
                          )}>
                            {milestone.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {milestone.due_date && (
                              <span className={clsx(
                                'text-[11px]',
                                urgency === 'overdue' ? 'text-danger-500 font-medium' :
                                urgency === 'soon' ? 'text-warning-500 font-medium' :
                                'text-gray-400 dark:text-gray-500'
                              )}>
                                {urgency === 'overdue' ? t('Overdue') + ' - ' : ''}
                                {formatDateTime(milestone.due_date)}
                              </span>
                            )}
                            {milestone.status === 'in_progress' && (
                              <Badge color="blue">{t('In Progress')}</Badge>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Actions - visible on hover */}
                    {!isEditing && (
                      <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <button onClick={() => handleStartEdit(milestone)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded" title={t('Edit')}>
                          <PiPencilSimpleLight className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(milestone.id)} className="p-1 text-gray-400 hover:text-danger-500 rounded" title={t('Delete')}>
                          <PiTrashLight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Add form */}
        {isAdding && (
          <div className={clsx('space-y-2', milestones.length > 0 && 'mt-3 pt-3 border-t border-hairline-soft')}>
            <input
              type="text"
              placeholder={t('Milestone title...')}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full px-2.5 py-1.5 text-sm border border-hairline rounded-lg bg-surface-sunken text-fg-body placeholder-fg-muted focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setIsAdding(false); setNewTitle(''); setNewDueDate(''); } }}
            />
            <input
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs border border-hairline rounded-lg bg-surface-sunken text-fg-body"
            />
            <div className="flex gap-1.5">
              <Button variant="primary" size="small" onClick={handleAdd} disabled={!newTitle.trim()}>{t('Add')}</Button>
              <Button variant="neutral" size="small" onClick={() => { setIsAdding(false); setNewTitle(''); setNewDueDate(''); }}>{t('Cancel')}</Button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  /*
    IN A PANE THE HEADER IS THE PANE'S.

    `paneAware` dropped the card's glass but kept its header row, so the pane
    titled "Milestones" carried a second bar also titled "Milestones" — the
    title rendered twice, one above the other. In a pane this component now
    renders its BODY alone: the title is the pane's, the add button is passed
    up to sit next to the close × (see `ProjectDetail`'s pane `actions`), and
    the tally moves to the footer where every other pane states its stat.
  */
  if (inPane) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">{body}</div>
        <PaneFooter
          stat={
            totalCount > 0
              ? t('{{done}} of {{total}} complete', { done: completedCount, total: totalCount })
              : t('No milestones yet')
          }
        >
          {addButton}
        </PaneFooter>
      </div>
    );
  }

  return (
    <GlassCard
      icon={<PiFlagPennantLight />}
      title={(
        <>
          {t('Milestones')}
          {tally && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">{tally}</span>
          )}
        </>
      )}
      padding="none"
      actions={addButton}
    >
      {body}
    </GlassCard>
  );
};

export default ProjectMilestones;
